/**
 * Fleet Overview - IoT Shack landing page
 *
 * Clickable stats, device types table with drill-down, emitting-now live ticker.
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import PageShell from '@/components/PageShell';
import StatsCard from '@/components/StatsCard';
import ActionButton from '@/components/ActionButton';
import LoadingState from '@/components/LoadingState';
import { fetchFleetSummary, fetchQuarantineSummary } from './api';
import type { FleetSummaryItem } from './types';
import { Cpu, Router, Activity, Radio, Bot, Wifi, ChevronRight, Circle, ShieldAlert } from 'lucide-react';
import { CP_BASE } from '../../lib/cpClient';
import { buildEventSourcePath } from '@/lib/eventSourceAuth';
import {
  EMITTING_NOW_WAIT_MS,
  emittingNowEmptyCopy,
  nextEmittingNowPhase,
} from './emittingNowHonesty';

function formatAge(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

export default function FleetOverview() {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['iot', 'fleet-summary'],
    queryFn: fetchFleetSummary,
    refetchInterval: 15000,
  });

  const qSummary = useQuery({
    queryKey: ['iot', 'quarantine-summary'],
    queryFn: fetchQuarantineSummary,
    refetchInterval: 30000,
    retry: false,
  });

  const models: FleetSummaryItem[] = data?.models || [];
  const totalDevices = data?.total_devices || 0;
  const totalOnline = models.reduce((sum, m) => sum + m.online, 0);
  const totalEmitting = models.reduce((sum, m) => sum + m.emitting, 0);
  const healthPct = totalDevices > 0 ? Math.round((totalOnline / totalDevices) * 100) : 0;
  const quarantineDepth = Number(qSummary.data?.new_count ?? 0);
  const oldestAge = qSummary.data?.oldest_age_seconds ?? null;
  const decoderUnresolved = Number(qSummary.data?.decoder_unresolved_count ?? 0);

  if (isLoading) {
    return (
      <PageShell title="IoT Shack" subtitle="Fleet overview, device types, and live telemetry." icon={Cpu} iconColor="bg-cyan-600">
        <LoadingState cards={4} />
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell title="IoT Shack" subtitle="Fleet overview." icon={Cpu} iconColor="bg-cyan-600">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">Failed to load fleet data: {String(error)}</p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="IoT Shack"
      subtitle="Fleet overview, device types, and live telemetry."
      icon={Cpu}
      iconColor="bg-cyan-600"
      actions={
        <div className="flex gap-2">
          <Link to="/iotshack/onboard">
            <ActionButton variant="primary" icon={Bot}>Onboard Device</ActionButton>
          </Link>
          <Link to="/iotshack/models">
            <ActionButton variant="secondary">View Catalog</ActionButton>
          </Link>
        </div>
      }
    >
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatsCard
          title="Total Devices"
          count={totalDevices}
          subtitle={`${models.length} device types`}
          icon={Cpu}
          color="bg-cyan-600"
          linkTo="/iotshack/models"
        />
        <StatsCard
          title="Online Now"
          count={totalOnline}
          subtitle={`${healthPct}% of fleet`}
          icon={Wifi}
          color="bg-green-600"
          linkTo="/iotshack/devices?status=online"
        />
        <StatsCard
          title="Emitting Data"
          count={totalEmitting}
          subtitle="In last 5 minutes"
          icon={Activity}
          color="bg-purple-600"
          linkTo="/iotshack/devices?emitting=true"
        />
        <StatsCard
          title="Device Types"
          count={models.length}
          subtitle="Configured models"
          icon={Router}
          color="bg-indigo-600"
          linkTo="/iotshack/models"
        />
      </div>

      {/* Sentinel senses — same quarantine-summary route (honest zeros) */}
      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 p-4">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="w-4 h-4 text-amber-700" />
          <h2 className="text-sm font-semibold text-amber-950">Quarantine & decoder health</h2>
          <span className="text-[10px] text-amber-700/80 font-mono">/api/telemetry/quarantine-summary</span>
        </div>
        {qSummary.isLoading ? (
          <div className="text-xs text-amber-800">Loading quarantine summary…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-md bg-white border border-amber-100 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-gray-500">Quarantine depth</div>
              <div className="text-2xl font-semibold text-gray-900 tabular-nums">{quarantineDepth}</div>
              <div className="text-[11px] text-gray-500">new entries (window)</div>
            </div>
            <div className="rounded-md bg-white border border-amber-100 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-gray-500">Oldest entry age</div>
              <div className="text-2xl font-semibold text-gray-900 tabular-nums">{formatAge(oldestAge)}</div>
              <div className="text-[11px] text-gray-500">
                {oldestAge == null ? 'empty — honest zero age' : 'since oldest in window'}
              </div>
            </div>
            <div className="rounded-md bg-white border border-amber-100 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-gray-500">decoder:unresolved</div>
              <div className="text-2xl font-semibold text-gray-900 tabular-nums">{decoderUnresolved}</div>
              <div className="text-[11px] text-gray-500">from summary / sources</div>
            </div>
          </div>
        )}
      </div>

      {/* Primary panels */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Device Types Table */}
        <div className="xl:col-span-2 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Device Types</h2>
            <Link to="/iotshack/onboard" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              + Add type
            </Link>
          </div>

          {models.length === 0 ? (
            <div className="p-12 text-center">
              <Cpu className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-medium text-gray-900 mb-1">No device types yet</h3>
              <p className="text-sm text-gray-500 mb-4">
                Onboard your first device type using the AI agent.
              </p>
              <Link to="/iotshack/onboard">
                <ActionButton variant="primary" icon={Bot}>Onboard Device</ActionButton>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-gray-500 uppercase tracking-wide border-b border-gray-100">
                    <th className="text-left px-6 py-3 font-medium">Model</th>
                    <th className="text-left px-4 py-3 font-medium">Type</th>
                    <th className="text-center px-4 py-3 font-medium">Running</th>
                    <th className="text-center px-4 py-3 font-medium">Offline</th>
                    <th className="text-center px-4 py-3 font-medium">Emitting</th>
                    <th className="text-right px-6 py-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {models.map((m, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => m.model_id && navigate(`/iotshack/models/${m.model_id}`)}
                    >
                      <td className="px-6 py-3">
                        <div className="text-sm font-medium text-gray-900">{m.model_name}</div>
                        {m.manufacturer && (
                          <div className="text-xs text-gray-500">{m.manufacturer}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
                          {m.type.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="text-center px-4 py-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/iotshack/devices?model=${m.model_id || m.type}&status=online`); }}
                          className="inline-flex items-center gap-1 text-sm font-semibold text-green-700 hover:text-green-800"
                        >
                          <Circle className="w-2 h-2 fill-green-500 text-green-500" />
                          {m.online}
                        </button>
                      </td>
                      <td className="text-center px-4 py-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/iotshack/devices?model=${m.model_id || m.type}&status=offline`); }}
                          className={`text-sm font-semibold ${m.offline > 0 ? 'text-red-600 hover:text-red-700' : 'text-gray-400'}`}
                        >
                          {m.offline}
                        </button>
                      </td>
                      <td className="text-center px-4 py-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/iotshack/devices?model=${m.model_id || m.type}&emitting=true`); }}
                          className="text-sm font-semibold text-purple-700 hover:text-purple-800"
                        >
                          {m.emitting}/{m.total}
                        </button>
                      </td>
                      <td className="text-right px-6 py-3">
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Emitting Now live ticker */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Emitting Now</h2>
          </div>
          <EmittingNowTicker censusEmitting={totalEmitting} />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        <QuickLink to="/iotshack/models" title="Catalog" description="Browse models" />
        <QuickLink to="/iotshack/playground" title="Playground" description="Inspect telemetry" />
        <QuickLink to="/iotshack/gateways" title="Gateways" description="Connectivity" />
        <QuickLink to="/iotshack/decoders" title="Decoders" description="Payload decoders" />
        <QuickLink to="/iotshack/listeners" title="Listeners" description="TCP/UDP/HTTP" />
        <QuickLink to="/iotshack/dashboards" title="Dashboards" description="Custom analytics" />
      </div>
    </PageShell>
  );
}

function QuickLink({ to, title, description }: { to: string; title: string; description: string }) {
  return (
    <Link
      to={to}
      className="flex flex-col p-3 rounded-lg border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all"
    >
      <span className="text-sm font-medium text-gray-900">{title}</span>
      <span className="text-xs text-gray-500">{description}</span>
    </Link>
  );
}

/**
 * EmittingNowTicker - SSE-powered live feed of recently emitting devices.
 * Times out: EventSource never errors on a hanging or heartbeat-only stream.
 */
function EmittingNowTicker({ censusEmitting = 0 }: { censusEmitting?: number }) {
  const [events, setEvents] = useState<Array<{
    device_id: string;
    type: string;
    manufacturer: string | null;
    model: string | null;
    iku_value: any;
    time: string;
  }>>([]);
  const [hasSseUrl, setHasSseUrl] = useState(true);
  const [streamOpen, setStreamOpen] = useState(false);
  const [streamError, setStreamError] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const esRef = useRef<EventSource | null>(null);
  const eventCountRef = useRef(0);

  const phase = nextEmittingNowPhase({
    hasSseUrl,
    eventCount: events.length,
    streamOpen,
    streamError,
    timedOut,
  });
  const copy = emittingNowEmptyCopy(phase);

  useEffect(() => {
    let cancelled = false;
    eventCountRef.current = 0;
    setEvents([]);
    setStreamOpen(false);
    setStreamError(false);
    setTimedOut(false);

    const sseUrl = buildEventSourcePath(`${CP_BASE}/iot/analytics/emitting-now`);
    if (!sseUrl) {
      setHasSseUrl(false);
      return;
    }
    setHasSseUrl(true);

    if (esRef.current) {
      esRef.current.close();
    }

    const es = new EventSource(sseUrl);
    esRef.current = es;

    const markOpen = () => {
      if (!cancelled) setStreamOpen(true);
    };
    es.onopen = markOpen;
    es.addEventListener('connected', markOpen);
    es.addEventListener('heartbeat', markOpen);

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.device_id) {
          eventCountRef.current += 1;
          setEvents((prev) => {
            const next = [data, ...prev.filter((p) => p.device_id !== data.device_id)];
            return next.slice(0, 30);
          });
        }
      } catch { /* ignore */ }
    };

    es.onerror = () => {
      if (cancelled) return;
      setStreamError(true);
      es.close();
      esRef.current = null;
    };

    const waitTimer = setTimeout(() => {
      if (cancelled) return;
      setTimedOut(true);
      if (eventCountRef.current === 0) {
        cancelled = true;
        es.close();
        esRef.current = null;
      }
    }, EMITTING_NOW_WAIT_MS);

    return () => {
      cancelled = true;
      clearTimeout(waitTimer);
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [retryNonce]);

  if (phase !== 'live') {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-gray-400">
        <Radio className="w-8 h-8 mb-2 opacity-30" />
        <p className="text-xs">{copy.title}</p>
        {copy.body ? (
          <p className="text-[11px] text-gray-500 mt-1 text-center max-w-[16rem]">{copy.body}</p>
        ) : null}
        {censusEmitting > 0 && (
          <p className="text-[11px] text-amber-700 mt-1">
            Fleet census says {censusEmitting} emitting — this ticker is a separate SSE stream, not that count.
          </p>
        )}
        {copy.retry ? (
          <button
            type="button"
            onClick={() => setRetryNonce((n) => n + 1)}
            className="mt-3 text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            Retry
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-50 max-h-[400px] overflow-y-auto">
      {events.map((evt, idx) => {
        const age = Math.round((Date.now() - new Date(evt.time).getTime()) / 1000);
        const stale = age > 300; // 5 min

        return (
          <Link
            key={`${evt.device_id}-${idx}`}
            to={`/iotshack/devices/${evt.device_id}`}
            className={`flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors ${stale ? 'opacity-50' : ''}`}
          >
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${stale ? 'bg-amber-400' : 'bg-green-500'}`} />
              <div>
                <div className="text-xs font-medium text-gray-900">{evt.device_id}</div>
                <div className="text-xs text-gray-400">{evt.type?.replace(/_/g, ' ')}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-700">
                {evt.iku_value && typeof evt.iku_value === 'object'
                  ? Object.entries(evt.iku_value).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(', ')
                  : ''}
              </div>
              <div className="text-xs text-gray-400">
                {age < 60 ? `${age}s ago` : age < 3600 ? `${Math.floor(age / 60)}m ago` : `${Math.floor(age / 3600)}h ago`}
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
